import { Link, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuthProfile } from "@/hooks/use-auth-profile"
import { trackUserLogout } from "@/lib/analytics-service"
import { signOutCurrentUser } from "@/lib/profile-service"

export function UserNav() {
  const navigate = useNavigate()
  const { user, profile, loading } = useAuthProfile()
  const [signingOut, setSigningOut] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  // Only render DropdownMenu after mount to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (loading) {
    return <Skeleton className="h-9 w-9 rounded-full" />
  }

  if (!user) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to="/login">Sign In</Link>
      </Button>
    )
  }

  const displayName = profile?.displayName || user.displayName || "User"
  const email = profile?.email || user.email || ""
  const avatarUrl = profile?.avatarURL || user.photoURL || undefined

  const handleSignOut = async () => {
    try {
      setSigningOut(true)
      trackUserLogout()
      await signOutCurrentUser()
      navigate("/login")
    } catch (error) {
      console.error("Failed to sign out", error)
    } finally {
      setSigningOut(false)
    }
  }

  // Render placeholder during SSR to maintain layout
  if (!isMounted) {
    const fallbackInitial = displayName ? displayName.slice(0, 1).toUpperCase() : "U"
    return (
      <Button variant="ghost" className="relative h-8 w-8 rounded-full" disabled>
        <Avatar className="h-9 w-9">
          <AvatarFallback>{fallbackInitial}</AvatarFallback>
        </Avatar>
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-9 w-9">
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={displayName} />
            ) : null}
            <AvatarFallback>{displayName.slice(0, 1).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            {email ? (
              <p className="text-xs leading-none text-muted-foreground">{email}</p>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/profile">Profile</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/support">Support</Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            if (!signingOut) {
              void handleSignOut()
            }
          }}
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
